import { STATUS_CODES } from '../constants';
import {
  selectFulfillmentGroup,
  getFulfillmentGroupStatusCode,
  IOpenedFrom,
} from './order-details';
import { IFulfillmentGroup, IOrderDetails } from '../models/order-details';

/**
 * Curbside and locker eligibility, read from the `orderDetails` response the
 * drawer fetches when it opens.
 *
 * Both flags live on the fulfillment group's `storePickup`, never on the order
 * root: `OrderDetailsAdapterImpl:2425` derives the root `isInLocker` from
 * `fulfillmentGroups.get(0)` alone, which is wrong whenever the pickup group is
 * not first.
 *
 * Neither rule consults `fulfillmentType`. `storePickup` only exists on a
 * genuine store-pickup group, so `isCurbsidePickupAvailable` and `IsInLocker`
 * have already answered the question the type string was being asked, and
 * `!IsInLocker` vs `IsInLocker` keeps the two predicates mutually exclusive on
 * their own. Consulting the type actively caused a bug: real OMS values with no
 * UI type behind them (`InStorePurchase`, `unavailable`, `Additional Items`)
 * resolve to `ShipToHome`, which failed the old allowlist and suppressed a
 * valid curbside or locker CTA.
 *
 * Both rules also require the pickup store's number, because both CTAs carry it
 * into a request: `CurbsidePickup` sends it as the `storeId` identifying where
 * to arrive, and `PickupLockerQRCode` refuses outright to call for a code
 * without one, rendering an error alert instead. Offering either button with no
 * store number can therefore only produce an error, so the absence of a number
 * makes the group ineligible rather than the CTA broken. A blank string counts
 * as absent — it reaches the API exactly as an empty one does.
 *
 * Status comes from the group, not the order root. The root code describes
 * whichever group `OrderDetailsAdapterImpl` looked at first, so on a mixed
 * order it can report a delivery group's progress while the pickup group is
 * waiting at the store. `getFulfillmentGroupStatusCode` reads the group's own
 * line items and falls back to the root, which is the same value on a
 * single-group order.
 *
 * Status is the numeric `statusCode` rather than a string. The rule used to run
 * against `FULFILLMENT_STATUSES`, and commit `d3549fa` folded
 * `PREPARING_FOR_PICKUP` (132) onto `BEING_PROCESSED` (`PROCESSING`, 101) when
 * it swapped codes for strings. That produced a sequence that was eligible
 * while processing, not eligible in transit, then eligible again at the store.
 * Against codes it is monotonic: eligible at 131 and not before.
 */

function hasPickupStoreNumber(group?: IFulfillmentGroup) {
  return Boolean(group?.storePickup?.store?.number?.trim());
}

export function isCurbsidePickupEligible(orderDetails?: IOrderDetails, openedFrom?: IOpenedFrom) {
  if (!orderDetails) return false;

  const group = selectFulfillmentGroup(orderDetails, openedFrom);
  const storePickup = group?.storePickup;

  return Boolean(
    storePickup?.isCurbsidePickupAvailable
    && !storePickup?.IsInLocker
    && hasPickupStoreNumber(group)
    && getFulfillmentGroupStatusCode(group, orderDetails) === STATUS_CODES.READY_FOR_PICKUP
  );
}

export function isLockerPickupEligible(
  orderDetails: IOrderDetails | undefined,
  isMobile: boolean,
  isConsumerApp: boolean,
  openedFrom?: IOpenedFrom,
) {
  const group = selectFulfillmentGroup(orderDetails, openedFrom);

  return Boolean(
    (isMobile || isConsumerApp)
    && group?.storePickup?.IsInLocker
    && hasPickupStoreNumber(group)
    && getFulfillmentGroupStatusCode(group, orderDetails) === STATUS_CODES.READY_FOR_PICKUP
  );
}
